import { useState, useEffect, useMemo, useCallback } from "react";
import _ from "lodash";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../../common/components/loading/loading";
import { getQualityAttributeValueHistory } from "../../../common/services/products/qualityAttributeValueService";
import { useTranslation } from "react-i18next";

const QualityAttributeValueHistory = ({ qualityAttributeValueId }) => {
  const { t } = useTranslation(["products"]);
  const [state, setState] = useState({
    qualityAttributeValueHistory: [],
    isLoading: true,
  });

  // Map API data to view model
  const mapToViewModel = useCallback((qualityAttributeValues) => {
    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };

    return qualityAttributeValues.map((qualityAttributeValue) => ({
      id: qualityAttributeValue.id,
      value: qualityAttributeValue.value,
      active: qualityAttributeValue.active,
      updatedByUserAccountId: qualityAttributeValue.createdByUserAccountId,
      updatedBy:
        qualityAttributeValue.updatedByUserAccountFirstName +
        " " +
        qualityAttributeValue.updatedByUserAccountLastName,
      updatedDate: new Date(
        qualityAttributeValue.updatedDate
      ).toLocaleDateString("en-US", dateFormatOptions),
      writeDate: new Date(qualityAttributeValue.writeDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      eventId: qualityAttributeValue.eventId,
    }));
  }, []);

  // Fetch quality attribute value history data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getQualityAttributeValueHistory(
          qualityAttributeValueId
        );
        const viewModelData = mapToViewModel(data);
        setState({
          qualityAttributeValueHistory: _.orderBy(viewModelData, "id", "desc"),
          isLoading: false,
        });
      } catch (error) {
        console.error("Error fetching quality attribute value history:", error);
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchData();
  }, [qualityAttributeValueId, mapToViewModel]);

  // Memoize column definitions to prevent unnecessary re-renders
  const columnDefs = useMemo(
    () => [
      { field: "id", headerName: t("RecordID"), sortable: true },
      {
        field: "value",
        headerName: t("QualityAttributeValue"),
        sortable: true,
      },
      {
        field: "active",
        headerName: t("Active"),
        sortable: true,
        cellClass: "ag-center-aligned-cell",
        flex: 1,
        cellRenderer: (params) => {
          const isActive =
            params.data?.active === true || params.data?.active === "true";
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <span className={isActive ? "text-success" : "text-danger"}>
                {isActive ? "Yes" : "No"}
              </span>
            </div>
          );
        },
      },
      { field: "updatedBy", headerName: t("UpdatedBy"), sortable: true },
      { field: "updatedDate", headerName: t("UpdateDate"), sortable: true },
      { field: "writeDate", headerName: t("WriteDate"), sortable: true },
      { field: "eventId", headerName: t("EventID"), sortable: true },
    ],
    [t]
  );

  // Default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
    }),
    []
  );

  // Create a getRowId function to ensure stable row identification
  const getRowId = useCallback((params) => params.data.id.toString(), []);

  if (state.isLoading) return <Loading />;

  return (
    <div className="history-table-container">
      {t("QualityAttributeValueID")} {qualityAttributeValueId}
      <ThemeableAgGrid
        className="table-extra-small"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={state.qualityAttributeValueHistory}
        getRowId={getRowId}
        animateRows={true}
        domLayout="autoHeight"
      />
    </div>
  );
};

export default QualityAttributeValueHistory;

